import {
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  WidthType,
  TextRun
} from 'docx';
import { downloadFilenameGenerator, sanitizeHeaderText } from '.';
import LABELS from '../../../ui-constants/en.json';
import AppConst from '../../../ui-constants/appConstants.json';
import { decodeHTML } from '../../../utils/commonUtils';

import { IQuery, RESPONSE_BY, ItableCntentProps } from '../../../models/IQueryType';

const noBorder = {
  style: BorderStyle.NONE,
  size: 0,
  color: 'ff0000'
};
const singleBorder = {
  style: BorderStyle.SINGLE,
  size: 1,
  color: 'e8e8ee'
};
const noBorders = {
  top: noBorder,
  bottom: noBorder,
  left: noBorder,
  right: noBorder
};

const tableCellStyling = {
  borders: {
    top: singleBorder,
    bottom: singleBorder,
    left: singleBorder,
    right: singleBorder
  },
  shading: {
    color: 'f7f7f8',
    fill: '000000',
    type: ShadingType.SOLID
  }
};
const emptyRow = new TableRow({
  children: [
    new TableCell({
      borders: noBorders,
      children: [new Paragraph('')]
    })
  ]
});

const seTokenError = (replyData: string) => {
  if (
    replyData?.includes(LABELS.TOKEN_ERR) ||
    replyData?.includes('https://aka.ms/oai/quotaincrease') ||
    replyData?.includes('SQL_ERROR')
  ) {
    return true;
  } else {
    return false;
  }
};

const parseFormattedLineChildren = (line: string): TextRun[] => {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.filter(Boolean).map((part) => {
    const boldMatch = part.match(/^\*\*(.*)\*\*$/);
    if (boldMatch) {
      return new TextRun({ text: boldMatch[1], bold: true });
    }
    return new TextRun({ text: part });
  });
};

const generateDocSummary = (
  line: string,
  type: string,
  totalLength: number = 0,
  ind: number,
  isParitallyAbortedResponse: boolean,
  isCompletelyAbortedResposne: boolean
) => {
  let modifiedSource: any = [];

  if (seTokenError(line)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.chat_reply.EMBROYNIC_ERROR }),
        new TextRun({ text: '', break: 1 }),
        ...modifiedSource
      ]
    });
  }

  if (RESPONSE_BY.STRATEGY_EDGE === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({
          text: line,
          color: isCompletelyAbortedResposne ? '656579' : '',
          italics: isCompletelyAbortedResposne ? true : false
        }),
        new TextRun({ text: '', break: 1 }),
        ...modifiedSource
      ]
    });
  }

  if (line.startsWith(LABELS.SORRY_ERR) && !line.startsWith(LABELS.SORRY_ERR1)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.GENERIC_OOPS_ERROR }),
        new TextRun({
          text: ' Source: Uploaded Document & Query',
          break: 1
        })
      ]
    });
  }

  if (RESPONSE_BY.STRATEGY_EDGE === type && line?.includes('Oops! No results found')) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        new TextRun({
          text: 'Source: Uploaded Document & Query',
          break: 1
        })
      ]
    });
  }

  const content = line.trim();

  const headerMatch = content.match(/^(#+)\s+(.*)/);
  if (headerMatch) {
    return new Paragraph({
      children: [new TextRun({ text: headerMatch[2], bold: true })]
    });
  }

  if (content.startsWith('- ')) {
    return new Paragraph({
      bullet: { level: 0 },
      children: parseFormattedLineChildren(content.substring(2))
    });
  }

  return new Paragraph({ children: parseFormattedLineChildren(content) });
};

const generateDocument = async (data: any, userImg: string | undefined, source: any) => {
  const IMAGES = {
    [RESPONSE_BY.USER]: userImg ? userImg : AppConst.AVATAR_IMAGE,
    [RESPONSE_BY.STRATEGY_EDGE]: AppConst.USER_AI_IMG
  };
  const paragraphs: any = [];

  const tableRowWithCol = (respData: any, respImg: any, respType?: any) => {
    const totalLength = decodeHTML(respData).split('\n');
    const { length } = totalLength;
    return paragraphs.push(
      new TableRow({
        children: [
          new TableCell({
            borders: noBorders,
            margins: {
              right: 200
            },
            children: [
              new Paragraph({
                children: [
                  new ImageRun({
                    data: respImg,
                    transformation: {
                      width: 30,
                      height: 30
                    }
                  })
                ]
              })
            ]
          }),

          new TableCell({
            borders: noBorders,
            children: [
              ...(respType !== 'user'
                ? decodeHTML(respData)
                    .split('\n')
                    .map((line: string, ind: number) => {
                      return generateDocSummary(
                        line.replace('\n\n', '\n'),
                        respType,
                        length,
                        ind,
                        respData?.isParitallyAbortedResponse,
                        respData?.isCompletelyAbortedResposne
                      );
                    })
                : [new Paragraph(respData?.replace(/\n\r+$/, '').trim()), new Paragraph('\n')])
            ]
          })
        ]
      })
    );
  };

  data.forEach(async (chat: any) => {
    const { summary, user } = chat;

    if (user) {
      tableRowWithCol(user, IMAGES[RESPONSE_BY.USER], RESPONSE_BY.USER);
    }

    if (summary) {
      tableRowWithCol(summary, IMAGES[RESPONSE_BY.STRATEGY_EDGE], RESPONSE_BY.STRATEGY_EDGE);
    }
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            size: '11pt',
            font: 'Calibri'
          }
        }
      },
      paragraphStyles: [
        {
          id: 'tableHeader',
          run: {
            bold: true
          }
        },
        {
          id: 'summary',
          quickFormat: true
        }
      ]
    },
    sections: [
      {
        children: [
          new Table({
            rows: paragraphs
          })
        ]
      }
    ]
  });
  return doc;
};

const exportToBYODWord = async (data: IQuery[], userImage?: string, source?: any) => {
  const clonedData = structuredClone(data);
  const doc = await generateDocument(clonedData, userImage, source);
  const blob = await Packer.toBlob(doc);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = downloadFilenameGenerator(RESPONSE_BY.URL, 'docx');
  link.click();
};

export default exportToBYODWord;
